import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { clientsAPI, agentsAPI } from '../lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  Robot, MagnifyingGlass, TrendUp, Lightning, CircleNotch, CheckCircle, Clock,
  Play, Pause, CalendarBlank, Checks, X, ArrowClockwise, Gear, Bell, Warning,
  ArrowUp, CaretDown
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function AgentsPage() {
  const [clients, setClients] = useState([]);
  const [activities, setActivities] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [runs, setRuns] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [runningScheduled, setRunningScheduled] = useState('');
  const [selectedAgent, setSelectedAgent] = useState('auditor');
  const [selectedClient, setSelectedClient] = useState('');
  const [taskInput, setTaskInput] = useState('');
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    client_id: '', agent_type: 'auditor', frequency: 'daily', auto_approve: false, enabled: true
  });
  const [recFilter, setRecFilter] = useState('pending');

  const loadAll = useCallback(async () => {
    try {
      const [c, a, s, r, rn, pc] = await Promise.all([
        clientsAPI.list(),
        agentsAPI.activity(),
        agentsAPI.schedules(),
        agentsAPI.recommendations(),
        agentsAPI.runs(),
        agentsAPI.pendingCount(),
      ]);
      setClients(c.data);
      setActivities(a.data);
      setSchedules(s.data);
      setRecommendations(r.data);
      setRuns(rn.data);
      setPendingCount(pc.data.count);
      if (c.data.length > 0 && !selectedClient) setSelectedClient(c.data[0].id);
    } catch {
      toast.error('Failed to load agent data');
    } finally {
      setLoading(false);
    }
  }, [selectedClient]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleTrigger = async () => {
    if (!taskInput.trim() || !selectedClient) return;
    setTriggering(true);
    try {
      const res = await agentsAPI.trigger({
        client_id: selectedClient,
        agent_type: selectedAgent,
        task_description: taskInput,
      });
      setActivities(prev => [res.data, ...prev]);
      setTaskInput('');
      toast.success(`${selectedAgent === 'auditor' ? 'Auditor' : 'Strategist'} completed task`);
    } catch {
      toast.error('Agent task failed');
    } finally {
      setTriggering(false);
    }
  };

  const handleRunScheduled = async (clientId) => {
    setRunningScheduled(clientId);
    try {
      const res = await agentsAPI.runScheduled(clientId);
      toast.success(`Generated ${res.data.total_recommendations} recommendations`);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scheduled run failed');
    } finally {
      setRunningScheduled('');
    }
  };

  const handleSaveSchedule = async () => {
    if (!scheduleForm.client_id) {
      toast.error('Select a client');
      return;
    }
    try {
      await agentsAPI.createSchedule(scheduleForm);
      toast.success('Schedule saved');
      setScheduleDialogOpen(false);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save schedule');
    }
  };

  const handleToggleSchedule = async (schedule) => {
    try {
      await agentsAPI.updateSchedule(schedule.id, { enabled: !schedule.enabled });
      loadAll();
    } catch {
      toast.error('Failed to update schedule');
    }
  };

  const handleToggleAutoApprove = async (schedule) => {
    try {
      await agentsAPI.updateSchedule(schedule.id, { auto_approve: !schedule.auto_approve });
      toast.success(`Auto-approve ${!schedule.auto_approve ? 'enabled' : 'disabled'}`);
      loadAll();
    } catch {
      toast.error('Failed to update');
    }
  };

  const handleActionRec = async (recId, action) => {
    try {
      await agentsAPI.actionRecommendation(recId, action);
      toast.success(`Recommendation ${action}d`);
      loadAll();
    } catch {
      toast.error('Failed to update recommendation');
    }
  };

  const handleBulkAction = async (action) => {
    try {
      const res = await agentsAPI.bulkAction(action);
      toast.success(res.data.message);
      loadAll();
    } catch {
      toast.error('Bulk action failed');
    }
  };

  const filteredRecs = recFilter === 'all'
    ? recommendations
    : recommendations.filter(r => r.status === recFilter);

  const getClientName = (id) => clients.find(c => c.id === id)?.name || 'Unknown';

  const impactColors = { high: 'nx-tag-red', medium: 'nx-tag-yellow', low: 'nx-tag-blue' };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[var(--accent-border)] border-t-[var(--accent)] rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="agents-page">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-[var(--text-primary)] tracking-tight">
              AI Agents
            </h1>
            <p className="text-sm text-[var(--text-secondary)] mt-1">Autonomous marketing intelligence powered by GPT-5.2</p>
          </div>
          {pendingCount > 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded bg-[var(--warning-bg)] border border-[var(--warning-border)]" data-testid="pending-badge">
              <Bell size={16} className="text-[var(--warning)]" weight="fill" />
              <span className="text-sm text-[var(--warning)] font-mono">{pendingCount} pending approval{pendingCount > 1 ? 's' : ''}</span>
            </div>
          )}
        </div>

        <Tabs defaultValue="recommendations" className="space-y-4">
          <TabsList className="bg-[var(--surface)] border border-[var(--border)]">
            <TabsTrigger value="recommendations" data-testid="tab-recommendations" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white">
              Recommendations
              {pendingCount > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 text-[10px] font-mono bg-[var(--warning)] text-black rounded-full">{pendingCount}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="schedules" data-testid="tab-schedules" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white">Schedules</TabsTrigger>
            <TabsTrigger value="runs" data-testid="tab-runs" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white">Run History</TabsTrigger>
            <TabsTrigger value="manual" data-testid="tab-manual" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white">Manual Trigger</TabsTrigger>
            <TabsTrigger value="activity" data-testid="tab-activity" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white">Activity Feed</TabsTrigger>
          </TabsList>

          {/* ═══ RECOMMENDATIONS TAB ═══ */}
          <TabsContent value="recommendations" className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2">
                {['pending', 'approved', 'rejected', 'all'].map(f => (
                  <button key={f} data-testid={`filter-${f}`}
                    onClick={() => setRecFilter(f)}
                    className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider rounded transition-colors duration-200 ${
                      recFilter === f ? 'bg-[var(--accent)] text-white' : 'bg-[var(--surface-sunken)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                    }`}
                  >
                    {f} {f === 'pending' && pendingCount > 0 ? `(${pendingCount})` : ''}
                  </button>
                ))}
              </div>
              {pendingCount > 0 && (
                <div className="flex items-center gap-2">
                  <Button data-testid="bulk-approve-btn" size="sm" onClick={() => handleBulkAction('approve')}
                    className="bg-[var(--success-bg)] text-[var(--success)] hover:bg-[var(--success-bg)] border border-[var(--success-border)]">
                    <Checks size={14} className="mr-1.5" /> Approve All
                  </Button>
                  <Button data-testid="bulk-reject-btn" size="sm" variant="ghost" onClick={() => handleBulkAction('reject')}
                    className="text-[var(--error)] hover:bg-[var(--error-bg)]">
                    <X size={14} className="mr-1.5" /> Reject All
                  </Button>
                </div>
              )}
            </div>

            {filteredRecs.length === 0 ? (
              <div className="nx-metric-card rounded-lg text-center py-12">
                <Robot size={40} className="mx-auto mb-3 text-[var(--text-tertiary)]" />
                <p className="text-[var(--text-secondary)]">{recFilter === 'pending' ? 'No pending recommendations. Run your agents to generate new ones!' : 'No recommendations found.'}</p>
                {clients.length > 0 && recFilter === 'pending' && (
                  <Button data-testid="run-agent-from-empty" className="mt-4 bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white" onClick={() => handleRunScheduled(clients[0].id)}>
                    <Play size={14} className="mr-2" /> Run Agents Now
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {filteredRecs.map((rec, i) => (
                  <div key={rec.id}
                    className="nx-metric-card rounded-lg animate-fade-in-up flex items-start gap-4"
                    style={{ animationDelay: `${i * 30}ms` }}
                    data-testid={`recommendation-${rec.id}`}
                  >
                    <div className={`w-10 h-10 rounded flex items-center justify-center flex-shrink-0 ${
                      rec.agent_type === 'auditor' ? 'bg-[var(--accent-light)]' : 'bg-[var(--success-bg)]'
                    }`}>
                      {rec.agent_type === 'auditor'
                        ? <MagnifyingGlass size={20} className="text-[var(--accent)]" weight="duotone" />
                        : <TrendUp size={20} className="text-[var(--success)]" weight="duotone" />
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h4 className="font-display text-sm font-bold text-[var(--text-primary)]">{rec.title}</h4>
                        <span className={`nx-tag ${impactColors[rec.impact] || 'nx-tag-gray'}`}>{rec.impact}</span>
                        <span className="nx-tag nx-tag-blue">{rec.category?.replace(/_/g, ' ')}</span>
                        {rec.auto_approved && <span className="nx-tag nx-tag-green">auto</span>}
                      </div>
                      <p className="text-xs text-[var(--text-secondary)] leading-relaxed mb-2">{rec.description}</p>
                      <div className="flex items-center gap-3 text-[10px] text-[var(--text-tertiary)] font-mono">
                        <span>{rec.client_name || getClientName(rec.client_id)}</span>
                        <span>&middot;</span>
                        <span>{rec.estimated_effort}</span>
                        <span>&middot;</span>
                        <span>{new Date(rec.created_at).toLocaleString()}</span>
                      </div>
                    </div>
                    {rec.status === 'pending' ? (
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button data-testid={`approve-rec-${rec.id}`}
                          onClick={() => handleActionRec(rec.id, 'approve')}
                          className="p-2 rounded bg-[var(--success-bg)] text-[var(--success)] hover:bg-[var(--success-bg)] transition-background-color duration-200">
                          <CheckCircle size={18} weight="fill" />
                        </button>
                        <button data-testid={`reject-rec-${rec.id}`}
                          onClick={() => handleActionRec(rec.id, 'reject')}
                          className="p-2 rounded bg-[var(--error-bg)] text-[var(--error)] hover:bg-[var(--error-bg)] transition-background-color duration-200">
                          <X size={18} weight="bold" />
                        </button>
                      </div>
                    ) : (
                      <span className={`nx-tag flex-shrink-0 ${rec.status === 'approved' ? 'nx-tag-green' : 'nx-tag-red'}`}>
                        {rec.status}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ═══ SCHEDULES TAB ═══ */}
          <TabsContent value="schedules" className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-sm font-bold text-[var(--text-primary)]">Agent Schedules</h3>
              <Button data-testid="add-schedule-btn" onClick={() => {
                setScheduleForm({ client_id: clients[0]?.id || '', agent_type: 'auditor', frequency: 'daily', auto_approve: false, enabled: true });
                setScheduleDialogOpen(true);
              }} className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white">
                <CalendarBlank size={14} className="mr-2" /> Add Schedule
              </Button>
            </div>

            {/* Quick Run buttons */}
            {clients.length > 0 && (
              <div className="nx-metric-card rounded-lg" data-testid="quick-run-panel">
                <h4 className="text-xs text-[var(--text-tertiary)] font-mono uppercase tracking-wider mb-3">Quick Run - Trigger All Enabled Schedules</h4>
                <div className="flex flex-wrap gap-2">
                  {clients.map(c => (
                    <Button key={c.id} data-testid={`run-scheduled-${c.id}`}
                      size="sm" variant="outline"
                      disabled={runningScheduled === c.id}
                      onClick={() => handleRunScheduled(c.id)}
                      className="border-[var(--border)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--accent)] disabled:opacity-50">
                      {runningScheduled === c.id ? <CircleNotch size={14} className="animate-spin mr-1.5" /> : <Play size={14} className="mr-1.5" />}
                      {c.display_name || c.name}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {schedules.length === 0 ? (
              <div className="nx-metric-card rounded-lg text-center py-12">
                <CalendarBlank size={40} className="mx-auto mb-3 text-[var(--text-tertiary)]" />
                <p className="text-[var(--text-secondary)]">No schedules configured. Add one to automate agent runs.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {schedules.map((s, i) => (
                  <div key={s.id} className="nx-metric-card rounded-lg animate-fade-in-up flex items-center gap-4"
                    style={{ animationDelay: `${i * 40}ms` }} data-testid={`schedule-${s.id}`}>
                    <div className={`w-10 h-10 rounded flex items-center justify-center ${
                      s.agent_type === 'auditor' ? 'bg-[var(--accent-light)]' : 'bg-[var(--success-bg)]'
                    }`}>
                      {s.agent_type === 'auditor'
                        ? <MagnifyingGlass size={20} className="text-[var(--accent)]" weight="duotone" />
                        : <TrendUp size={20} className="text-[var(--success)]" weight="duotone" />
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-sm text-[var(--text-primary)] font-medium">{getClientName(s.client_id)}</span>
                        <span className={`nx-tag ${s.agent_type === 'auditor' ? 'nx-tag-blue' : 'nx-tag-green'}`}>{s.agent_type}</span>
                        <span className="nx-tag nx-tag-gray">{s.frequency}</span>
                      </div>
                      <div className="flex items-center gap-3 text-[10px] text-[var(--text-tertiary)] font-mono">
                        <span>Runs: {s.total_runs || 0}</span>
                        <span>&middot;</span>
                        <span>Last: {s.last_run ? new Date(s.last_run).toLocaleString() : 'Never'}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-[var(--text-tertiary)] font-mono uppercase">Auto-approve</span>
                        <Switch data-testid={`auto-approve-${s.id}`}
                          checked={s.auto_approve}
                          onCheckedChange={() => handleToggleAutoApprove(s)}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-[var(--text-tertiary)] font-mono uppercase">Enabled</span>
                        <Switch data-testid={`toggle-schedule-${s.id}`}
                          checked={s.enabled}
                          onCheckedChange={() => handleToggleSchedule(s)}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Schedule Dialog */}
            <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
              <DialogContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text-primary)]" data-testid="schedule-dialog">
                <DialogHeader>
                  <DialogTitle className="font-display">Configure Agent Schedule</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <label className="block text-xs text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Client</label>
                    <Select value={scheduleForm.client_id} onValueChange={v => setScheduleForm({...scheduleForm, client_id: v})}>
                      <SelectTrigger data-testid="schedule-client-select" className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]">
                        <SelectValue placeholder="Select client" />
                      </SelectTrigger>
                      <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
                        {clients.map(c => <SelectItem key={c.id} value={c.id} className="text-[var(--text-primary)] hover:bg-[var(--surface-sunken)]">{c.display_name || c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-xs text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Agent Type</label>
                    <Select value={scheduleForm.agent_type} onValueChange={v => setScheduleForm({...scheduleForm, agent_type: v})}>
                      <SelectTrigger data-testid="schedule-agent-select" className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
                        <SelectItem value="auditor" className="text-[var(--text-primary)] hover:bg-[var(--surface-sunken)]">The Auditor (SEO)</SelectItem>
                        <SelectItem value="strategist" className="text-[var(--text-primary)] hover:bg-[var(--surface-sunken)]">The Strategist (Ads)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-xs text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Frequency</label>
                    <Select value={scheduleForm.frequency} onValueChange={v => setScheduleForm({...scheduleForm, frequency: v})}>
                      <SelectTrigger data-testid="schedule-frequency-select" className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
                        <SelectItem value="daily" className="text-[var(--text-primary)] hover:bg-[var(--surface-sunken)]">Daily</SelectItem>
                        <SelectItem value="weekly" className="text-[var(--text-primary)] hover:bg-[var(--surface-sunken)]">Weekly</SelectItem>
                        <SelectItem value="manual" className="text-[var(--text-primary)] hover:bg-[var(--surface-sunken)]">Manual Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded border border-[var(--border)]">
                    <div>
                      <p className="text-sm text-[var(--text-primary)]">Auto-Approve Recommendations</p>
                      <p className="text-xs text-[var(--text-tertiary)] mt-0.5">Enable once agent predictions are proven reliable</p>
                    </div>
                    <Switch data-testid="schedule-auto-approve-toggle"
                      checked={scheduleForm.auto_approve}
                      onCheckedChange={v => setScheduleForm({...scheduleForm, auto_approve: v})}
                    />
                  </div>
                  <Button data-testid="save-schedule-btn" onClick={handleSaveSchedule} className="w-full bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white">
                    Save Schedule
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* ═══ RUN HISTORY TAB ═══ */}
          <TabsContent value="runs" className="space-y-4">
            <div className="nx-metric-card rounded-lg" data-testid="run-history-panel">
              <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-4">
                <ArrowClockwise size={18} className="inline mr-2 text-[var(--accent)]" weight="duotone" />
                Agent Run History
              </h3>
              {runs.length === 0 ? (
                <p className="text-[var(--text-secondary)] text-center py-8">No runs yet. Configure schedules and trigger your first run.</p>
              ) : (
                <table className="nx-table">
                  <thead>
                    <tr><th>Agent</th><th>Client</th><th>Status</th><th>Recommendations</th><th>Started</th><th>Duration</th></tr>
                  </thead>
                  <tbody>
                    {runs.map(r => {
                      const duration = r.completed_at && r.started_at
                        ? Math.round((new Date(r.completed_at) - new Date(r.started_at)) / 1000)
                        : null;
                      return (
                        <tr key={r.id}>
                          <td>
                            <span className={`nx-tag ${r.agent_type === 'auditor' ? 'nx-tag-blue' : 'nx-tag-green'}`}>{r.agent_type}</span>
                          </td>
                          <td className="!text-[var(--text-primary)]">{r.client_name || getClientName(r.client_id)}</td>
                          <td>
                            <span className="flex items-center gap-1.5">
                              {r.status === 'completed' ? (
                                <CheckCircle size={14} className="text-[var(--success)]" weight="fill" />
                              ) : r.status === 'running' ? (
                                <CircleNotch size={14} className="text-[var(--warning)] animate-spin" />
                              ) : (
                                <Warning size={14} className="text-[var(--error)]" weight="fill" />
                              )}
                              <span className="text-xs">{r.status}</span>
                            </span>
                          </td>
                          <td className="font-mono text-[var(--accent)]">{r.recommendations_count || 0}</td>
                          <td className="font-mono text-xs">{new Date(r.started_at).toLocaleString()}</td>
                          <td className="font-mono text-xs">{duration !== null ? `${duration}s` : '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>

          {/* ═══ MANUAL TRIGGER TAB ═══ */}
          <TabsContent value="manual" className="space-y-4">
            {/* Agent Trigger */}
            <div className="nx-metric-card rounded-lg" data-testid="agent-trigger-panel">
              <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-4">
                <Lightning size={18} className="inline mr-2 text-[var(--accent)]" weight="fill" />
                Manual Agent Task
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <Select value={selectedAgent} onValueChange={setSelectedAgent}>
                  <SelectTrigger data-testid="agent-type-select" className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
                    <SelectItem value="auditor" className="text-[var(--text-primary)] hover:bg-[var(--surface-sunken)]">The Auditor (SEO)</SelectItem>
                    <SelectItem value="strategist" className="text-[var(--text-primary)] hover:bg-[var(--surface-sunken)]">The Strategist (Ads)</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={selectedClient} onValueChange={setSelectedClient}>
                  <SelectTrigger data-testid="agent-client-select" className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]">
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
                    {clients.map(c => <SelectItem key={c.id} value={c.id} className="text-[var(--text-primary)] hover:bg-[var(--surface-sunken)]">{c.display_name || c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input data-testid="agent-task-input" value={taskInput}
                  onChange={e => setTaskInput(e.target.value)}
                  placeholder="Describe the task..." className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)]"
                  onKeyDown={e => { if (e.key === 'Enter') handleTrigger(); }}
                />
                <Button data-testid="trigger-agent-btn" onClick={handleTrigger}
                  disabled={triggering || !taskInput.trim()}
                  className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white disabled:opacity-50">
                  {triggering ? (
                    <span className="flex items-center gap-2"><CircleNotch size={16} className="animate-spin" /> Running...</span>
                  ) : (
                    <span className="flex items-center gap-2"><Lightning size={16} /> Run Agent</span>
                  )}
                </Button>
              </div>
            </div>

            {/* Agent Personas */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="nx-metric-card rounded-lg" data-testid="auditor-panel">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded bg-[var(--accent-light)] flex items-center justify-center">
                    <MagnifyingGlass size={22} className="text-[var(--accent)]" weight="duotone" />
                  </div>
                  <div>
                    <h3 className="font-display text-[var(--text-primary)] font-bold">The Auditor</h3>
                    <p className="text-xs text-[var(--text-tertiary)]">Expert Technical SEO | 10yr Experience</p>
                  </div>
                  <div className="flex-1" />
                  <span className="nx-status-dot running" />
                  <span className="text-xs text-[var(--success)] ml-1">Active</span>
                </div>
                <p className="text-xs text-[var(--text-secondary)] leading-relaxed">
                  Crawls client sites daily. Identifies broken links, 404s, Core Web Vitals issues, and schema markup gaps. Generates actionable recommendations with impact scoring.
                </p>
              </div>
              <div className="nx-metric-card rounded-lg" data-testid="strategist-panel">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded bg-[var(--success-bg)] flex items-center justify-center">
                    <TrendUp size={22} className="text-[var(--success)]" weight="duotone" />
                  </div>
                  <div>
                    <h3 className="font-display text-[var(--text-primary)] font-bold">The Strategist</h3>
                    <p className="text-xs text-[var(--text-tertiary)]">Performance Marketer | ROI Specialist</p>
                  </div>
                  <div className="flex-1" />
                  <span className="nx-status-dot running" />
                  <span className="text-xs text-[var(--success)] ml-1">Active</span>
                </div>
                <p className="text-xs text-[var(--text-secondary)] leading-relaxed">
                  Analyzes ad performance across Meta/Google/TikTok. Detects ad fatigue, identifies budget reallocation opportunities, and recommends audience optimizations.
                </p>
              </div>
            </div>
          </TabsContent>

          {/* ═══ ACTIVITY FEED TAB ═══ */}
          <TabsContent value="activity" className="space-y-4">
            <div className="nx-metric-card rounded-lg" data-testid="all-activity-feed">
              <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-4">All Agent Activity</h3>
              {activities.length === 0 ? (
                <p className="text-[var(--text-secondary)] text-center py-8">No activity yet</p>
              ) : (
                <div className="space-y-2">
                  {activities.map((a, i) => (
                    <div key={a.id} className="flex items-start gap-3 p-3 rounded border border-[var(--border-light)] animate-slide-down"
                      style={{ animationDelay: `${i * 20}ms` }}>
                      {a.status === 'completed' ? (
                        <CheckCircle size={16} className="text-[var(--success)] mt-0.5 flex-shrink-0" weight="fill" />
                      ) : a.status === 'running' ? (
                        <CircleNotch size={16} className="text-[var(--warning)] mt-0.5 flex-shrink-0 animate-spin" />
                      ) : (
                        <Clock size={16} className="text-[var(--text-tertiary)] mt-0.5 flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className={`nx-tag ${a.agent_type === 'auditor' ? 'nx-tag-blue' : 'nx-tag-green'}`}>{a.agent_type}</span>
                          <span className="text-[10px] text-[var(--text-tertiary)] font-mono">{new Date(a.created_at).toLocaleString()}</span>
                        </div>
                        <p className="text-xs text-[var(--text-primary)] leading-relaxed">{a.task}</p>
                        {a.result && <p className="text-xs text-[var(--text-secondary)] mt-1 whitespace-pre-wrap leading-relaxed">{a.result}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
